import React, { useState, useEffect } from "react";
import { getBillingInfo, BillingInfo, getCustomerBillingPortalAccess, listInvoices, Invoice, getInvoicePaymentLink } from "../api/billing";
import { getCompany, Company } from "../api/company";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { ReactComponent as CreditCard } from "../assets/icons/credit-card.svg";
import { ReactComponent as EditIcon } from "../assets/icons/edit.svg";
import { useAuth } from "../context/AuthContext";

const Billing: React.FC = () => {
  const { isActionRequired, checkBillingStatus } = useAuth();
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManagePaymentLoading, setIsManagePaymentLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [company, setCompany] = useState<Company | null>(null);
  const [payingInvoiceId, setPayingInvoiceId] = useState<string | null>(null);
  const [copiedInvoiceId, setCopiedInvoiceId] = useState<string | null>(null);

  const handleCopyInvoiceHandle = async (invoice: Invoice) => {
    try {
      await navigator.clipboard.writeText(invoice.invoiceHandle);
      setCopiedInvoiceId(invoice.id);
      setTimeout(() => setCopiedInvoiceId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const getDisplayStatus = (state: Invoice['state']) => {
    if (state === "Settled") return "Settled";
    if (state === "Failed") return "Failed";
    if (state === "Cancelled") return "Cancelled";
    return "Pending";
  };

  // Determine if unpaid invoices alert should be shown
  const showUnpaidAlert = React.useMemo(() => {
    if (!billingInfo || invoices.length === 0) return false;

    // Check for failed invoices
    const hasFailedInvoice = invoices.some(inv => inv.state === "Failed");

    // Check for any unpaid invoices
    const hasUnpaidInvoice = invoices.some(inv =>
      ["Created", "Pending", "Dunning"].includes(inv.state)
    );

    return billingInfo.actionRequired || hasFailedInvoice || hasUnpaidInvoice;
  }, [billingInfo, invoices]);

  useEffect(() => {
    fetchBillingInfo();
    fetchInvoices();
    fetchCompany();
  }, []);

  const fetchCompany = async () => {
    try {
      const response = await getCompany();
      setCompany(response);
    } catch (err) {
      console.error("Error fetching company:", err);
    }
  };

  const fetchBillingInfo = async () => {
    try {
      setLoading(true);
      const response = await getBillingInfo();
      setBillingInfo(response.billingInfo);
      setError(null);

      // Only update global billing status if we need to clear the alert
      if (!response.billingInfo?.actionRequired && isActionRequired) {
        await checkBillingStatus();
      }
    } catch (err) {
      setError("Failed to fetch billing information");
      console.error("Error fetching billing info:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoadingInvoices(true);
      const response = await listInvoices();
      setInvoices(response.invoices);
      setHasMore(response.hasMore);
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleManagePaymentMethods = async () => {
    try {
      setIsManagePaymentLoading(true);
      const response = await getCustomerBillingPortalAccess();
      window.open(response.customerPortalUrl, "_blank");
    } catch (err) {
      setError("Failed to access billing portal");
      console.error("Error accessing billing portal:", err);
    } finally {
      setIsManagePaymentLoading(false);
    }
  };

  const handlePayInvoice = async (invoice: Invoice) => {
    try {
      setPayingInvoiceId(invoice.id);
      const response = await getInvoicePaymentLink(invoice.invoiceHandle);
      if (response.paymentUrl) {
        window.location.href = response.paymentUrl;
      } else {
        setError("Failed to get payment link");
      }
    } catch (err) {
      setError("Failed to get payment link");
      console.error("Error getting payment link:", err);
    } finally {
      setPayingInvoiceId(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!billingInfo) {
    return (
      <div className="w-full bg-yellow-100 border-l-4 border-yellow-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-yellow-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              Billing for this account has not been configured by administrator
              yet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showUnpaidAlert && (
        <div className="mb-6 w-full bg-yellow-100 border-l-4 border-yellow-500 p-4">
          <div className="flex items-center">
            <svg
              className="h-5 w-5 text-yellow-500 flex-shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <p className="ml-3 text-sm text-yellow-700">
              You have unpaid invoices that require immediate attention.
            </p>
          </div>
        </div>
      )}

      {billingInfo.paymentMethods.filter((method) =>
        ["Active", "Pending"].includes(method.state)
      ).length === 0 && (
        <div className="mb-6 w-full bg-primary/10 border-l-4 border-primary p-4">
          <div className="flex items-center">
            <CreditCard className="h-5 w-5 text-primary flex-shrink-0" />
            <p className="ml-3 text-sm text-primary">
              No active payment method. Add a payment method to enable automatic invoice payments.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium mb-4">Company</h3>
        {company ? (
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm text-gray-500">Company Name</p>
              <p className="text-gray-900">{company.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Company CVR</p>
              <p className="text-gray-900">{company.cvrNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Contact Email</p>
              <p className="text-gray-900">{company.email}</p>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">Loading company information...</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Invoices</h3>
          <Button
            variant="light"
            className="gap-2"
            onClick={handleManagePaymentMethods}
            disabled={isManagePaymentLoading}
          >
            {isManagePaymentLoading ? (
              <>
                <Loader className="w-4 h-4" />
                Loading...
              </>
            ) : (
              <>
                <EditIcon className="w-4 h-4" />
                Payment Methods
              </>
            )}
          </Button>
        </div>
        {loadingInvoices ? (
          <div className="flex justify-center py-4">
            <Loader />
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No invoices found</p>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_100px] gap-4 px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium">
              <div>Invoice Handle</div>
              <div>Created</div>
              <div>Amount</div>
              <div>Status</div>
              <div></div>
            </div>
            
            {invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="grid grid-cols-[2fr_1fr_1fr_1fr_100px] gap-4 px-4 py-3 bg-white border rounded-lg items-center hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-1 text-gray-900">
                  {invoice.invoiceHandle}
                  <button
                    onClick={() => handleCopyInvoiceHandle(invoice)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title="Copy to clipboard"
                  >
                    {copiedInvoiceId === invoice.id ? (
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="text-gray-900">
                  {formatDate(invoice.created)}
                </div>
                <div className="text-gray-900">{`${invoice.amount} ${invoice.currency}`}</div>
                <div>
                  <span
                    className={`text-sm px-2 py-1 rounded ${
                      getDisplayStatus(invoice.state) === "Pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : getDisplayStatus(invoice.state) === "Failed"
                        ? "bg-red-100 text-red-800"
                        : getDisplayStatus(invoice.state) === "Settled"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {getDisplayStatus(invoice.state)}
                  </span>
                </div>
                <div className="flex justify-end">
                  {["Created", "Pending", "Dunning", "Failed"].includes(invoice.state) ? (
                    <Button
                      variant="primary"
                      className="gap-2 text-sm py-1 px-3"
                      onClick={() => handlePayInvoice(invoice)}
                      disabled={payingInvoiceId === invoice.id}
                    >
                      {payingInvoiceId === invoice.id ? (
                        <>
                          <Loader className="w-4 h-4" />
                          Loading...
                        </>
                      ) : (
                        "Pay now"
                      )}
                    </Button>
                  ) : invoice.state === "Settled" ? (
                    <Button
                      variant="light"
                      className="text-sm py-1 px-3 opacity-50 cursor-default"
                      disabled
                    >
                      Paid
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}

            {invoices.length > 0 && hasMore && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={handleManagePaymentMethods}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  See More
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;
